import { LIMITS } from '@weblite/permission'
import { Timestamp } from '@weblite/ts-action'
import { styled, Typography } from '@mui/material'
import { FormEvent, useCallback, useState } from 'react'
import {
  asyncDelay,
  SECOND_MS,
  useCountDownToDate,
} from '@weblite/logic-toolkit'
import { TextInput } from '$common/input'
import { Alert } from '$common/input/alert'
import { PrimaryButton } from '$common/buttons'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { t } from '$i18n'
import { getSubscription, joinHallViaLink } from '$logic/subscription'
import { closeRoomListSearch } from '$logic/router'
import {
  showCouldntJoinClassSnackbar,
  showInvalidLinkSnackbar,
} from '$comp/snackbars'
import { getHallByInviteLink } from '$logic/hall'
import { openRoom } from '$logic/opened-room'

const INVALID_LINK_ERROR_DURATION = 5 * SECOND_MS
const formatInvalidLinkError = (remainingMS: number) =>
  t('joinClassField.invalidLinkError')(Math.round(remainingMS / 1000)) as string

let disabledUntilGlobal: Timestamp = 0

const openClass = async (roomId: string) => {
  closeRoomListSearch()
  await asyncDelay(300)
  openRoom(roomId)
}

export function JoinClassField() {
  const [disabledUntil, setDisabledUntil] =
    useState<Timestamp>(disabledUntilGlobal)
  const [loading, setLoading] = useState(false)

  const [link, setLink] = useState('')

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setLoading(true)
      try {
        const inviteLinkArr = link.trim().split('/')
        const inviteLink = inviteLinkArr[inviteLinkArr.length - 1].slice(1)

        if (!inviteLink || inviteLink.length !== LIMITS.inviteLinkSize) {
          showInvalidLinkSnackbar()
          return
        }

        const res = await getHallByInviteLink(inviteLink)
        if (!res?.room) {
          disabledUntilGlobal = dateWithOffset() + INVALID_LINK_ERROR_DURATION
          setDisabledUntil(disabledUntilGlobal)
          return
        }

        const { room } = res
        // NOTE: If client is member of hall, redirect to hall
        if (await getSubscription(room.id)) {
          setLink('')
          openClass(room.id)
          return
        }
        const isJoined = await joinHallViaLink(inviteLink)
        if (isJoined) {
          setLink('')
          openClass(room.id)
        } else showCouldntJoinClassSnackbar()
      } finally {
        setLoading(false)
      }
    },
    [link],
  )

  const invalidLinkError = useCountDownToDate(
    disabledUntil || 0,
    dateWithOffset,
    undefined,
    formatInvalidLinkError,
  )

  return (
    <Root>
      <Form onSubmit={handleSubmit}>
        <Typography
          component="p"
          variant="subtitle3"
          color="neutral600"
          mb="8px"
        >
          {t('joinClassField.title')}
        </Typography>

        <FieldsWrapper sx={{ mb: '8px' }}>
          <TextInput
            placeholder={t('joinClassField.placeholder')}
            onChange={setLink}
            value={link}
            size="small"
            rootSx={{ flexShrink: 1, mb: 0, mt: 0 }}
          />

          <PrimaryButton
            disabled={
              !link || !!(disabledUntil && disabledUntil > dateWithOffset())
            }
            text={
              <Typography variant="subtitle3" color="neutral000">
                {t('joinClassField.submitText')}
              </Typography>
            }
            type="submit"
            sx={{ height: '40px', minWidth: '94px' }}
            loading={loading}
          />
        </FieldsWrapper>

        {Boolean(disabledUntil) && disabledUntil > dateWithOffset() && (
          <Alert variant="error" text={invalidLinkError} />
        )}
      </Form>
    </Root>
  )
}

const Root = styled('div')`
  width: 100%;
  padding: 16px;
`

const Form = styled('form')`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.palette.neutral300};
`

const FieldsWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`
